import { useTranslation } from "react-i18next";
import { Lock } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StatusBadge } from "@/components/ListPageTemplate";
import { STATUS_COLORS } from "@shared/schema";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { formatDateTime } from "@/lib/date-utils";
import { PcOrder, getPcOrderStatus } from "@/hooks/use-pc-orders";
import { usePcOrderStatusLabel } from "@/components/pc/pc-order-status";

interface PcOrderDetailDialogProps {
  order: PcOrder | null;
  isOpen: boolean;
  onClose: () => void;
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </div>
      <div className="text-base">{value}</div>
    </div>
  );
}

/**
 * Read-only order summary for PC staff. Order contents and pricing are
 * managed in Eclipse; this dialog only displays what was submitted.
 */
export function PcOrderDetailDialog({
  order,
  isOpen,
  onClose,
}: PcOrderDetailDialogProps) {
  const { t } = useTranslation();
  const formatPcOrderStatus = usePcOrderStatusLabel();

  if (!order) return null;

  const products = Array.isArray(order.products) ? order.products : [];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 space-y-1">
          <div className="flex items-start justify-between gap-3 pr-8">
            <DialogTitle className="text-2xl font-bold">
              {order.title}
            </DialogTitle>
            <StatusBadge
              status={getPcOrderStatus(order)}
              statusColors={STATUS_COLORS}
              formatStatus={formatPcOrderStatus}
            />
          </div>
          <p className="text-base font-medium text-muted-foreground text-left">
            {order.contractor.name}
          </p>
        </DialogHeader>

        <div className="px-6 pb-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border-t pt-4">
          {order.createdByName && (
            <DetailField
              label={t("pages.pcOrders.detail.customer")}
              value={order.createdByName}
            />
          )}
          {order.contractor.phone && (
            <DetailField
              label={t("pages.pcOrders.detail.phone")}
              value={order.contractor.phone}
            />
          )}
          {order.contractor.hajocaCustomerId && (
            <DetailField
              label={t("pages.pcOrders.detail.customerId")}
              value={order.contractor.hajocaCustomerId}
            />
          )}
          <DetailField
            label={t("pages.pcOrders.detail.submitted")}
            value={formatDateTime(order.submittedAt)}
          />
        </div>

        <div className="px-6 border-t pt-4">
          <div className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground pb-2">
            <span>{t("pages.pcOrders.detail.item")}</span>
            <span className="text-right">{t("pages.pcOrders.detail.qty")}</span>
            <span className="text-right w-16">
              {t("pages.pcOrders.detail.unit")}
            </span>
            <span className="text-right w-20">
              {t("pages.pcOrders.detail.amount")}
            </span>
          </div>
          <div className="divide-y">
            {products.map((product, index) => (
              <div
                key={`${product.hajocaProductId}-${index}`}
                className="grid grid-cols-[1fr_auto_auto_auto] gap-x-4 items-center py-3"
              >
                <div className="min-w-0">
                  <p className="font-medium truncate">{product.description}</p>
                  <p className="text-sm text-muted-foreground font-mono">
                    {product.hajocaProductId}
                  </p>
                </div>
                <span className="text-right text-muted-foreground">
                  {product.quantity}
                </span>
                <span className="text-right w-16 font-mono text-muted-foreground">
                  ${formatCentsAsDollars(product.unitPrice)}
                </span>
                <span className="text-right w-20 font-mono font-semibold">
                  ${formatCentsAsDollars(product.totalPrice)}
                </span>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between border-t py-4">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("pages.pcOrders.detail.orderTotal")}
            </span>
            <span className="text-2xl font-bold">
              ${formatCentsAsDollars(order.totalOrderCost)}
            </span>
          </div>
        </div>

        <div className="px-6 py-4 bg-muted/50 border-t rounded-b-lg">
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Lock className="h-4 w-4 flex-shrink-0" />
            {t("pages.pcOrders.detail.viewOnlyNotice")}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
